import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, KeyRound } from 'lucide-react';
import FinancePanel from './modules/Module08';
import ConfirmButton from '../components/ConfirmButton';
import {
  verifyPassword,
  getStoredHash,
  hasPassword,
  setUnlocked,
  isUnlocked,
  savePassword,
  clearVault,
} from '../utils/financeAuth';

const inputCls =
  'w-full bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder-gray-600 outline-none focus:border-white/40 tracking-wide';

function PassInput({ label, value, onChange, placeholder, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="text-[9px] tracking-[0.25em] uppercase text-gray-500 mb-2 block">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className={inputCls + ' pr-12'}
        />
        <button type="button" onClick={() => setShow((s) => !s)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] tracking-[0.15em] uppercase text-gray-500 hover:text-white">
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}

/** Full-page lock — enter code to open Module 08 */
function LockPage({ onUnlock, onChangePassword, onForgot }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  function submit(e) {
    e.preventDefault();
    setError('');
    if (!code.trim()) return;
    setChecking(true);
    const ok = verifyPassword(code.trim(), getStoredHash());
    setChecking(false);
    if (ok) {
      setUnlocked(true);
      onUnlock();
    } else {
      setError('Incorrect password. Try again.');
      setCode('');
    }
  }

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-2">
      <Lock size={40} className="mb-6" style={{ color: 'var(--gold)' }} />
      <p className="text-[9px] tracking-[0.4em] uppercase mb-2" style={{ color: 'var(--gold)' }}>
        Module 08 · Private Vault
      </p>
      <h1 className="text-2xl font-light tracking-[0.12em] uppercase text-center mb-2">
        Enter Your Code
      </h1>
      <p className="text-sm text-gray-500 text-center max-w-xs mb-8 leading-relaxed">
        Income & budget are protected. Enter your password to open this module.
      </p>

      <form onSubmit={submit} className="w-full max-w-sm space-y-4">
        <PassInput label="Password" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="Enter code" autoFocus />

        {error && <p className="text-[11px] text-red-400 text-center">{error}</p>}

        <button type="submit" disabled={checking || !code.trim()}
          className="w-full text-[10px] tracking-[0.35em] uppercase font-semibold py-4 disabled:opacity-40"
          style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
          {checking ? 'Checking…' : 'Unlock Module'}
        </button>
      </form>

      <div className="w-full max-w-sm mt-8 pt-8 border-t border-white/10 space-y-4 text-center">
        <button type="button" onClick={onChangePassword}
          className="text-[9px] tracking-[0.2em] uppercase text-gray-500 hover:text-white transition-colors">
          Change code →
        </button>
        <div>
          <ConfirmButton
            onConfirm={onForgot}
            label="Forgot code"
            confirmLabel="Tap to confirm — this erases the entries"
            className="block w-full text-[9px] tracking-[0.15em] uppercase transition-colors hover:text-red-400"
            style={{ color: '#4b5563' }}
            confirmStyle={{ color: '#f87171' }}
            timeoutMs={6000}
          />
          <p className="text-[9px] text-gray-700 mt-2 leading-relaxed">
            The code is not an encryption key, so resetting it erases this
            device&rsquo;s entries. Restore them afterwards from a backup file.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Sets the vault code: old / new / repeat, or new / repeat on first use. */
function ChangePasswordPage({ onDone, onCancel, firstTime }) {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [repeatPwd, setRepeatPwd] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  function submit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!firstTime && !verifyPassword(oldPwd, getStoredHash())) {
      setError('Old code is incorrect.');
      return;
    }
    if (newPwd.length < 4) {
      setError('New code must be at least 4 characters.');
      return;
    }
    if (newPwd !== repeatPwd) {
      setError('New password and repeat password do not match.');
      return;
    }

    savePassword(newPwd);
    setSuccess(firstTime ? 'Code created.' : 'Code updated.');
    setTimeout(() => onDone(true), 800);
  }


  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-2">
      <KeyRound size={36} className="mb-6" style={{ color: 'var(--gold)' }} />
      <p className="text-[9px] tracking-[0.4em] uppercase mb-2" style={{ color: 'var(--gold)' }}>
        Security Settings
      </p>
      <h1 className="text-2xl font-light tracking-[0.12em] uppercase text-center mb-2">
        {firstTime ? 'Create a Code' : 'Change Code'}
      </h1>
      <p className="text-sm text-gray-500 text-center max-w-sm mb-8 leading-relaxed">
        {firstTime
          ? 'Choose a code for this module. It is stored on this device only, so keep a note of it — it cannot be recovered.'
          : 'Enter your current code, then choose a new one.'}
      </p>

      <form onSubmit={submit} className="w-full max-w-sm space-y-5">
        {!firstTime && (
          <PassInput label="Current Code" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)}
            placeholder="Current code" autoFocus />
        )}
        <PassInput label="New Code" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
          placeholder="New code" autoFocus={firstTime} />
        <PassInput label="Repeat Code" value={repeatPwd} onChange={(e) => setRepeatPwd(e.target.value)}
          placeholder="Repeat new code" />

        {error && <p className="text-[11px] text-red-400 text-center">{error}</p>}
        {success && <p className="text-[11px] text-center" style={{ color: 'var(--gold)' }}>{success}</p>}

        <button type="submit"
          className="w-full text-[10px] tracking-[0.35em] uppercase font-semibold py-4"
          style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
          {firstTime ? 'Create Code' : 'Save New Code'}
        </button>

        {!firstTime && (
          <button type="button" onClick={onCancel}
            className="w-full text-[9px] tracking-[0.2em] uppercase text-gray-500 hover:text-white py-3 transition-colors">
            Cancel
          </button>
        )}
      </form>
    </div>
  );
}

export default function FinancePage() {
  const navigate = useNavigate();
  // loading | setup | lock | change | open
  const [view, setView] = useState('loading');

  useEffect(() => {
    // No code on this device yet means first use, so the owner sets one
    // rather than inheriting a default that ships in the source.
    if (!hasPassword()) { setView('setup'); return; }
    setView(isUnlocked() ? 'open' : 'lock');
  }, []);

  function forgot() {
    clearVault();
    setView('setup');
  }

  function lock() {
    setUnlocked(false);
    setView('lock');
  }

  return (
    <div className="min-h-screen bg-black text-white max-w-lg mx-auto px-6 pb-16 safe-top safe-bottom">

      {/* Page header — separate from module view */}
      <header className="pt-6 pb-6 border-b border-white/10">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft size={14} />
          <span className="text-[9px] tracking-[0.25em] uppercase">Back to Dashboard</span>
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[9px] tracking-[0.35em] uppercase mb-2" style={{ color: 'var(--gold)' }}>
              Module 08
            </p>
            <h1 className="text-xl font-light tracking-[0.12em] uppercase">Income & Budget</h1>
            <p className="text-[9px] tracking-[0.15em] uppercase text-gray-600 mt-2">
              Private ledger · this device only
            </p>
          </div>
          {view === 'open' && (
            <button
              onClick={() => setView('change')}
              className="text-[9px] tracking-[0.15em] uppercase border border-white/15 px-3 py-2 hover:border-white/40 transition-colors shrink-0"
            >
              Password
            </button>
          )}
        </div>
      </header>

      <main className="pt-8">
        {view === 'loading' && (
          <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 text-center py-20 animate-pulse">
            Loading secure module…
          </p>
        )}

        {view === 'lock' && (
          <LockPage
            onUnlock={() => setView('open')}
            onChangePassword={() => setView('change')}
            onForgot={forgot}
          />
        )}

        {view === 'setup' && (
          <ChangePasswordPage firstTime onDone={() => setView('open')} onCancel={() => {}} />
        )}

        {view === 'change' && (
          <ChangePasswordPage
            onDone={(stayOpen) => setView(stayOpen ? 'open' : 'lock')}
            onCancel={() => setView(isUnlocked() ? 'open' : 'lock')}
          />
        )}

        {view === 'open' && (
          <FinancePanel onLock={lock} onChangePassword={() => setView('change')} />
        )}
      </main>
    </div>
  );
}
